import { useTranslation } from 'next-i18next';
import React from 'react';

import { CardBrand } from '../../../../components/Payments/CardBrand';
import { useFlow } from '../../../../providers';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import {
  GeneratedPaymentMethodCardWallet,
  GeneratedPaymentMethodWalletType,
} from '../../../../types/generated';
import { useAnalyticsEmitter } from '../../../../utils/analytics/emitter';
import { PaymentType } from '../../types';
import { CardButton } from '../shared/CardButton';

function generateWallet(
  paymentMethodType: string | undefined,
): GeneratedPaymentMethodCardWallet | null {
  switch (paymentMethodType) {
    case 'apple_pay':
      return { type: GeneratedPaymentMethodWalletType.ApplePay };

    case 'google_pay':
      return { type: GeneratedPaymentMethodWalletType.GooglePay };

    default:
      return null;
  }
}

interface PaymentMethodButtonProps {
  disabled?: boolean;
  isPreOrder: boolean;
}

const cryptoAssetUrl = '/images/components/crypto_com.svg';

export const PaymentMethodButton: React.FC<PaymentMethodButtonProps> = ({
  disabled,
  isPreOrder,
}) => {
  const drawController = usePreAuthDrawCtx();
  const {
    draw,
    newPaymentMethodType,
    paymentErrorCode,
    paymentMethods,
    selectedPaymentType,
    usingNewPaymentMethod,
  } = drawController;

  const [emitter] = useAnalyticsEmitter();
  const { t } = useTranslation(['product', 'countries']);
  const { goToStep } = useFlow();

  const cryptoComEnabled = Boolean(draw?.cryptoComEnabled && !isPreOrder);

  const paymentErrorHint = t(
    'product:drawers.review.draw.paymentMethod.paymentMethodCardFailure',
  );
  const selectedPaymentMethod = paymentMethods.find(
    (p) => p.providerPaymentMethodId === drawController.selectedPaymentMethodId,
  );

  const handleClick = () => {
    emitter(
      paymentMethods.length > 0 ? 'edit payment' : 'add payment',
      'clicked',
    );
    if (cryptoComEnabled) {
      goToStep('select_payment_type');
      return;
    }
    if (paymentMethods.length > 0) {
      goToStep('select_payment_method');
    } else {
      goToStep('add_payment_method');
    }
  };

  if (selectedPaymentType === PaymentType.CRYPTO) {
    return (
      <CardButton
        color={paymentErrorCode ? 'warning' : 'default'}
        disabled={disabled}
        hint={paymentErrorCode ? paymentErrorHint : void 0}
        icon="edit"
        onClick={handleClick}
      >
        <span className="flex items-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt="crypto"
            className="h-4 w-6 overflow-hidden rounded-sm border border-divider/20"
            src={cryptoAssetUrl}
          />
          {t('product:drawers.review.draw.paymentMethod.usingCrypto')}
        </span>
      </CardButton>
    );
  }

  if (usingNewPaymentMethod) {
    return (
      <CardButton
        color={paymentErrorCode ? 'warning' : 'default'}
        disabled={disabled}
        hint={paymentErrorCode ? paymentErrorHint : void 0}
        icon="edit"
        onClick={handleClick}
      >
        <span className="flex items-center gap-3">
          <CardBrand
            brand="fallback"
            wallet={generateWallet(newPaymentMethodType)}
          />
          {t('product:drawers.review.draw.paymentMethod.usingNewCard')}
        </span>
      </CardButton>
    );
  }

  if (!selectedPaymentMethod) {
    return (
      <CardButton
        color={paymentErrorCode ? 'warning' : 'default'}
        disabled={disabled}
        hint={paymentErrorCode ? paymentErrorHint : void 0}
        icon="chevron"
        onClick={handleClick}
      >
        <span className="text-neutral-4">
          {t('product:drawers.review.draw.paymentMethod.addButton')}
        </span>
      </CardButton>
    );
  }

  return (
    <CardButton
      color={paymentErrorCode ? 'warning' : 'default'}
      disabled={disabled}
      hint={paymentErrorCode ? paymentErrorHint : void 0}
      icon="edit"
      onClick={handleClick}
    >
      <span className="flex gap-3">
        {selectedPaymentMethod.card && (
          <>
            <CardBrand brand={selectedPaymentMethod.card.brand} />
            <span>∙∙∙∙ ∙∙∙∙ ∙∙∙∙ {selectedPaymentMethod.card.last4}</span>
          </>
        )}
        {!selectedPaymentMethod.card && <span>Unknown payment method</span>}
      </span>
    </CardButton>
  );
};
